import { ScreenSizer } from '@react-three/drei'
import { MeshProps, useLoader } from '@react-three/fiber'
import { Suspense } from 'react'
import { Euler, Group, MathUtils, Matrix4, Mesh, Object3DEventMap, Plane, TextureLoader, Vector3 } from 'three'
import { POSITIVE_Z } from '~/components/DrawingCanvas/constants/dimensions'

type RotationControlMeshProps = {
  radius?: number
  offset?: number
  tiltGroupOffset?: Vector3
  targetElement: Group<Object3DEventMap> | Mesh
  onCommit(newRotation: number): void
} & MeshProps

export const RotateIconTexture = () => {
  const texture = useLoader(TextureLoader, new URL(`/src/assets/textures/drag_handles.jpg`, import.meta.url).href)
  return (
    <>
      <meshBasicMaterial color="black" attach="material-0" />
      <meshBasicMaterial map={texture} toneMapped={false} attach="material-1" />
      <meshBasicMaterial map={texture} toneMapped={false} attach="material-2" />
    </>
  )
}

export const RotationHandle = ({
  radius = 16,
  offset = 0,
  targetElement,
  onCommit,
  tiltGroupOffset,
  ...meshProps
}: RotationControlMeshProps) => {
  const plane = new Plane()
  const intersection = new Vector3()
  let origin: Vector3 | null = null
  const mtx = new Matrix4()
  return (
    <ScreenSizer position-x={offset + radius / 4}>
      <mesh
        {...meshProps}
        onPointerDown={e => {
          e.stopPropagation()
          const isLeftClick = e.button === 0
          if (!isLeftClick) return
          const target: HTMLElement = e.target as HTMLElement
          target.setPointerCapture(e.pointerId)
          origin = new Vector3().setFromMatrixPosition(targetElement.matrixWorld)
        }}
        onPointerMove={e => {
          e.stopPropagation()
          if (!origin) return
          plane.setFromNormalAndCoplanarPoint(POSITIVE_Z, origin)
          e.ray.intersectPlane(plane, intersection)
          intersection.sub(origin)
          const deltaAngle = Math.atan2(intersection.y, intersection.x)
          const clampedDegrees = Math.round(MathUtils.radToDeg(deltaAngle))
          targetElement.matrixAutoUpdate = false
          targetElement.matrix.identity()
          targetElement.matrix.multiply(mtx.makeRotationY(MathUtils.degToRad(clampedDegrees)))
          if (tiltGroupOffset) targetElement.matrix.multiply(mtx.makeTranslation(tiltGroupOffset))
        }}
        onPointerUp={e => {
          e.stopPropagation()
          origin = null
          const target: HTMLElement = e.target as HTMLElement
          target.releasePointerCapture(e.pointerId)
          const { y } = new Euler().setFromRotationMatrix(targetElement.matrix)
          onCommit(MathUtils.radToDeg(y))
        }}
      >
        <cylinderGeometry args={[radius, radius]} />
        <Suspense fallback={<meshBasicMaterial color="white" />}>
          <RotateIconTexture />
        </Suspense>
      </mesh>
    </ScreenSizer>
  )
}
